import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Ligne_Document_Vente } from '../models/models'; // Define this model
import api from './api';

interface LigneDocumentVenteState {
  ligneDOcData: Ligne_Document_Vente[];
  selectedLigne: Ligne_Document_Vente | null;
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  // requestId de la dernière requête de LISTE dispatchée (fetchLignes / fetchLignesByDocumentId).
  // fetchLignesByDocumentId est dispatché depuis 3 endroits indépendants (page Lignes au montage,
  // refreshLignes() du formulaire après chaque ajout/modif/suppression, NextDocDispatch après
  // dispatch) sans annulation mutuelle. Sans garde, une réponse ENVOYÉE avant mais qui ARRIVE
  // après (ralentie par les autres appels réseau du montage) écrasait un état plus frais — la
  // ligne qu'on venait d'insérer disparaissait du tableau bien qu'elle soit en base. On ignore
  // toute réponse dont le requestId ne correspond plus à la dernière requête dispatchée.
  currentListRequestId: string | null;
}

const initialState: LigneDocumentVenteState = {
  ligneDOcData: [],
  selectedLigne: null,
  total: 0,
  totalPages: 0,
  currentPage: 0,
  loading: false,
  error: null,
  successMessage: null,
  currentListRequestId: null,
};

// Thunks

export const fetchLignes = createAsyncThunk<
  { data: Ligne_Document_Vente[]; total: number; totalPages: number; currentPage: number },
  { document_vente_id: number; limit: number; offset: number },
  { rejectValue: string }
>(
  'lignes/fetchLignes',
  async ({ document_vente_id, limit, offset }, thunkAPI) => {
    try {
      const response = await api.get('/ligneDocumentVentes/getAll', {
        params: { document_vente_id, limit, offset },
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to fetch data.');
    }
  }
);

export const fetchLignesByDocumentId = createAsyncThunk<
   { data: Ligne_Document_Vente[]; total: number; totalPages: number; currentPage: number },
  { document_vente_id: number; limit: number; offset: number },
  { rejectValue: string }
>('lignes/fetchByDocumentId', async ({document_vente_id, limit, offset }, thunkAPI) => {
  try {
    const response = await api.get('/ligneDocumentVentes/getAll', {
      params: {
        document_vente_id,
        limit,
        offset,
      },
    });
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.error || error.response?.data?.message || 'Failed to fetch lignes for document.'
    );
  }
});

export const fetchLigneById = createAsyncThunk<
  Ligne_Document_Vente,
  number,
  { rejectValue: string }
>('lignes/fetchById', async (id, thunkAPI) => {
  try {
    // Route backend : GET /ligneDocumentVentes/:id (pas /getById/:id — 404 sinon)
    const response = await api.get(`/ligneDocumentVentes/${id}`);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to fetch by ID.');
  }
});

export const createOrUpdateLigne = createAsyncThunk<
  Ligne_Document_Vente,
  Ligne_Document_Vente,
  { rejectValue: string }
>('lignes/createOrUpdate', async (ligne, thunkAPI) => {
  try {
    const response = await api.post('/ligneDocumentVentes/createOrUpdate', ligne);
    // response.data inclut un champ transient `warnings?: string[]` rempli côté
    // backend (_save) quand stock négatif autorisé ou sous seuil de sécurité.
    // Le formulaire appelant lit result.warnings pour afficher la snackbar.
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to create or update.');
  }
});

export const importLignesExcel = createAsyncThunk<
  {
    success: number;
    errors: Array<{ row: number; message: string }>;
    articles_manquants?: Array<{ code: string; lignes: number[] }>;
    ignorees?: number[];   // n° de ligne du fichier sautées (quantité vide ou 0, hors inventaire)
  },
  { document_vente_id: number; depot_id: number; lignes: Record<string, any>[] },
  { rejectValue: string }
>('lignes/importExcel', async (payload, thunkAPI) => {
  try {
    const response = await api.post('/ligneDocumentVentes/import', payload);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.error || error.response?.data?.message || 'Erreur lors de l\'import'
    );
  }
});

export const deleteLigne = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('lignes/delete', async (id, thunkAPI) => {
  try {
    await api.delete('/ligneDocumentVentes', { params: { id } });
    return id;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to delete.');
  }
});

// Slice

const ligneDocumentVenteSlice = createSlice({
  name: 'lignes',
  initialState,
  reducers: {
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
    },
    setSelectedLigne(state, action: PayloadAction<Ligne_Document_Vente | null>) {
      state.selectedLigne = action.payload;
    },
    clearMessages(state) {
      state.error = null;
      state.successMessage = null;
    },
    clearLigneData(state) {
      state.ligneDOcData = [];
      state.selectedLigne = null; // évite qu'une ligne d'un ancien document persiste
      state.total = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLignes.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentListRequestId = action.meta.requestId;
      })
      .addCase(fetchLignes.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentListRequestId) return; // réponse périmée, ignorée
        state.loading = false;
        state.ligneDOcData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchLignes.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentListRequestId) return;
        state.loading = false;
        state.error = action.payload || 'Error fetching lignes';
      })
      .addCase(fetchLignesByDocumentId.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentListRequestId = action.meta.requestId;
      })
      .addCase(fetchLignesByDocumentId.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentListRequestId) return; // réponse périmée, ignorée
        state.loading = false;
        state.ligneDOcData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchLignesByDocumentId.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentListRequestId) return;
        state.loading = false;
        state.error = action.payload || 'Failed to fetch lignes for document.';
      })
      .addCase(fetchLigneById.fulfilled, (state, action) => {
        state.selectedLigne = action.payload;
      })
      .addCase(createOrUpdateLigne.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrUpdateLigne.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'LigneDocumentVente saved successfully!';
      })
      .addCase(createOrUpdateLigne.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error saving ligne';
      })
      .addCase(deleteLigne.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteLigne.fulfilled, (state, action) => {
        state.loading = false;
        state.ligneDOcData = state.ligneDOcData.filter((l:Ligne_Document_Vente) => l.id !== action.payload);
        state.successMessage = 'Deleted successfully!';
      })
      .addCase(deleteLigne.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error deleting ligne';
      });
  },
});

export const {
  setCurrentPage,
  setSelectedLigne,
  clearMessages,
  clearLigneData,
} = ligneDocumentVenteSlice.actions;

export default ligneDocumentVenteSlice.reducer;
